"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { handleMultiSelect } from "./utils";
import type { AnalyticsFilters } from "./types";

const hideMissingOptions = (options: string[]) =>
  options.filter((option) => !option.includes("Missing"));

const HIDDEN_CREDITOR_TYPE_OPTIONS = new Set([
  "Banks, Bilateral Official, Multilateral Official",
  "Banks, Multilateral Official",
  "Capital markets, Private Bond Holder",
]);

const hideExcludedCreditorTypeOptions = (options: string[]) =>
  options.filter((option) => !HIDDEN_CREDITOR_TYPE_OPTIONS.has(option));

const CREDITOR_TYPE_LABEL_MAP: Record<string, string> = {
  "Banks, Bilateral Official": "Multiple Creditor Types",
  "Capital markets": "Capital Markets",
};

const getCreditorTypeLabel = (value: string): string =>
  CREDITOR_TYPE_LABEL_MAP[value] ?? value;

type FiltersProvenanceProps = {
  filters: AnalyticsFilters;
  setFilters: (next: AnalyticsFilters) => void;
  documentAreaOptions: string[];
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  creditorTypeColumns: string[];
  categoryOptions: string[];
  minYear: number;
  maxYear: number;
  sticky?: boolean;
  horizontal?: boolean;
  noCard?: boolean;
};

export default function FiltersProvenance({
  filters,
  setFilters,
  documentAreaOptions,
  creditorRegionOptions,
  borrowerRegionOptions,
  creditorTypeColumns,
  categoryOptions,
  minYear,
  maxYear,
  sticky = true,
  horizontal = false,
  noCard = false,
}: FiltersProvenanceProps) {
  const visibleCreditorTypeColumns = hideExcludedCreditorTypeOptions(
    hideMissingOptions(creditorTypeColumns),
  );

  const documentAreaSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Region</InputLabel>
      <Select
        multiple
        value={filters.selectedDocumentAreas ?? []}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedDocumentAreas: next }),
          )
        }
        input={<OutlinedInput label="Document Area" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {documentAreaOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={(filters.selectedDocumentAreas ?? []).includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Region</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorRegions: next }),
          )
        }
        input={<OutlinedInput label="Creditor Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {creditorRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const borrowerRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Borrower Region</InputLabel>
      <Select
        multiple
        value={filters.selectedBorrowerRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedBorrowerRegions: next }),
          )
        }
        input={<OutlinedInput label="Borrower Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {borrowerRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedBorrowerRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorTypeSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Type</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorTypes}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorTypes: next }),
          )
        }
        input={<OutlinedInput label="Creditor Type" />}
        renderValue={(selected) =>
          selected.map((value) => getCreditorTypeLabel(value)).join(", ")
        }
      >
        {visibleCreditorTypeColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorTypes.includes(option)}
            />
            <ListItemText primary={getCreditorTypeLabel(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const categorySelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Institution</InputLabel>
      <Select
        multiple
        value={filters.selectedCategories ?? []}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCategories: next }),
          )
        }
        input={<OutlinedInput label="Category" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {categoryOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={(filters.selectedCategories ?? []).includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeSlider = (
    <Box sx={horizontal ? { width: "300px" } : {}}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0 }}>
        Date Range: {filters.yearRange[0]} - {filters.yearRange[1]}
      </Typography>
      <Slider
        value={filters.yearRange}
        min={minYear}
        max={maxYear}
        onChange={(_, value) =>
          setFilters({ ...filters, yearRange: value as number[] })
        }
        valueLabelDisplay="auto"
      />
    </Box>
  );

  const handleReset = () => {
    setFilters({
      selectedDocumentAreas: [],
      selectedCreditorRegions: [],
      selectedBorrowerRegions: [],
      selectedCreditorTypes: [],
      selectedCategories: [],
      selectedDenominations: [], // not used here
      yearRange: [minYear, maxYear],
      completeDataOnly: false,
    });
  };

  const resetButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={handleReset}
      sx={{ minWidth: 92 }}
    >
      Reset
    </Button>
  );

  const inner = horizontal ? (
    <Stack spacing={2.2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(3, 1fr)",
          },
          gap: 2,
        }}
      >
        {documentAreaSelect}
        {/* {creditorRegionSelect}
        {borrowerRegionSelect} */}
        {categorySelect}
        {creditorTypeSelect}

      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {dateRangeSlider}
        {resetButton}
      </Box>
    </Stack>
  ) : (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Filters
      </Typography>
      <Stack spacing={2.2}>
        {documentAreaSelect}
        {/* {creditorRegionSelect} */}
        {/* {borrowerRegionSelect} */}
        {categorySelect}
        {creditorTypeSelect}
        {dateRangeSlider}
        {resetButton}
      </Stack>
    </>
  );

  if (noCard) {
    return inner;
  }

  return (
    <Card
      sx={{
        height: "fit-content",
        position: sticky ? { lg: "sticky" } : "static",
        top: sticky ? { lg: 16 } : "auto",
      }}
    >
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
